<template>
  <b-section-loader :loading="$loading.isLoading('internalStaffUnitSingle')">
    <v-card>
      <v-card-text class="d-flex flex-column gap-4">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center gap-3">
            <v-btn
              color="light-blue"
              density="comfortable"
              icon
              readonly
              rounded="lg"
              variant="flat">
              <v-icon color="light-blue-darken-3">mdi-account-outline</v-icon>
            </v-btn>
            <div class="d-flex flex-column">
              <h2>{{ internalStaffUnit?.name }}</h2>
              <span class="text-disabled">
                {{ internalStaffUnit?.department?.name }} ·
                {{ internalStaffUnit?.position?.name }} ·
                {{ internalStaffUnit?.staffUnitType?.name }}
              </span>
            </div>
          </div>
          <div class="d-flex align-center gap-2">
            <v-chip
              :color="hasActiveAssignment ? 'green' : 'grey'"
              class="font-weight-bold"
              size="small">
              {{ hasActiveAssignment ? 'Зайнято' : 'Вакантна' }}
            </v-chip>
            <v-btn
              :disabled="hasActiveAssignment"
              class="text-none"
              @click="addAssignment">
              Призначити
            </v-btn>
          </div>
        </div>
        <v-divider></v-divider>

        <div v-if="hasActiveAssignment">
          <div class="font-weight-bold font-size-15 mb-2">
            Поточне призначення
          </div>
          <v-card border color="green" variant="tonal">
            <v-card-text class="d-flex align-center justify-space-between">
              <div class="d-flex align-center ga-3">
                <v-avatar color="green-darken-4" size="36">
                  <span class="text-white font-weight-bold text-caption">
                    {{
                      getInitials(internalStaffUnit?.activeAssignment?.employee)
                    }}
                  </span>
                </v-avatar>
                <div class="d-flex flex-column">
                  <span class="text-body-1 font-weight-bold text-black">
                    {{
                      getPersonName(
                        internalStaffUnit?.activeAssignment?.employee,
                        { initials: true }
                      )
                    }}
                  </span>
                  <span class="text-green-darken-4">
                    {{
                      internalStaffUnit?.activeAssignment?.appointmentType?.name
                    }}
                    · з
                    {{
                      new CustomDate(
                        internalStaffUnit?.activeAssignment?.dateFrom
                      ).toString()
                    }}
                  </span>
                </div>
              </div>
              <div class="d-flex align-center gap-2">
                <span class="font-weight-bold text-green-darken-4"
                  >Поточний</span
                >
                <v-btn
                  class="text-none"
                  density="comfortable"
                  variant="tonal"
                  @click="editAssignment(internalStaffUnit.activeAssignment)">
                  Редагувати
                </v-btn>
                <v-btn
                  class="text-none"
                  color="error"
                  density="comfortable"
                  variant="tonal"
                  :loading="$loading.isLoading('dismissAssignment')"
                  @click="dismissAssignment">
                  Звільнити
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <div v-if="futureAssignments.length">
          <div class="font-weight-bold font-size-15 mb-2">
            Майбутні призначення
          </div>
          <div class="d-flex flex-column gap-2">
            <v-card
              v-for="assignment in futureAssignments"
              :key="assignment.id"
              border
              color="blue"
              variant="tonal">
              <v-card-text class="d-flex align-center justify-space-between">
                <div class="d-flex align-center ga-3">
                  <v-avatar color="blue-darken-2" size="36">
                    <span class="text-white font-weight-bold text-caption">
                      {{
                        getPersonName(assignment.employee, { initials: true })
                      }}
                    </span>
                  </v-avatar>
                  <div class="d-flex flex-column">
                    <span class="text-body-1 font-weight-bold">
                      {{
                        getPersonName(assignment.employee, { initials: true })
                      }}
                    </span>
                    <span class="text-blue-darken-2">
                      {{ assignment.appointmentType?.name }} · з
                      {{ new CustomDate(assignment.dateFrom).toString() }}
                    </span>
                  </div>
                </div>
                <div class="d-flex align-center gap-2">
                  <v-chip class="font-weight-bold" color="blue" size="small">
                    Заплановано
                  </v-chip>
                  <v-btn
                    class="text-none"
                    density="comfortable"
                    variant="tonal"
                    @click="editAssignment(assignment)">
                    Редагувати
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>

        <div>
          <div class="mb-2">
            <span class="font-weight-bold font-size-15">
              Історія призначень
            </span>
            <span class="text-disabled">({{ historyAssignments.length }})</span>
          </div>
          <div
            v-if="!historyAssignments.length"
            class="text-disabled text-body-2">
            Попередніх призначень немає
          </div>
          <div v-else class="d-flex flex-column gap-2">
            <v-card
              v-for="assignment in historyAssignments"
              :key="assignment.id"
              border
              color="white"
              style="filter: grayscale(100%)"
              variant="tonal">
              <v-card-text class="d-flex align-center justify-space-between">
                <div class="d-flex align-center ga-3">
                  <v-avatar color="green-lighten-4" size="36">
                    <span class="text-white font-weight-bold text-caption">
                      {{
                        getPersonName(assignment.employee, { initials: true })
                      }}
                    </span>
                  </v-avatar>
                  <div class="d-flex flex-column">
                    <span class="text-body-1 text-disabled">
                      {{
                        getPersonName(assignment.employee, { initials: true })
                      }}
                    </span>
                    <span class="text-disabled">
                      {{ assignment.appointmentType?.name }} ·
                      {{ new CustomDate(assignment.dateFrom).toString() }} —
                      {{ new CustomDate(assignment.dateTo).toString() }}
                    </span>
                  </div>
                </div>
                <v-btn
                  class="text-none"
                  density="comfortable"
                  variant="tonal"
                  @click="editAssignment(assignment)">
                  Редагувати
                </v-btn>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </b-section-loader>
</template>

<script lang="ts">
import { initInternalStaffUnit } from './internalStaffUnit'
import { getPersonName } from 'best-modules/utils/helpers'
import { CustomDate } from 'best-modules/utils'
import { BSectionLoader } from 'best-modules/components'
import { UserLink } from '@/utils/types/user'

export default {
  name: 'InternalStaffUnitSingle',
  components: {
    BSectionLoader,
  },
  data() {
    return {
      CustomDate,
    }
  },
  methods: {
    getPersonName,
  },
  setup() {
    const getInitials = (employee?: UserLink) => {
      return `${employee?.name?.[0] || ''}${employee?.patronymic?.[0] || ''}`
    }
    return {
      ...initInternalStaffUnit(),
      getInitials,
    }
  },
}
</script>

<style lang="scss" scoped></style>
